import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
} from "react";
import { Field, ErrorMessage } from "formik";

const CMSFormDateRange = forwardRef((props, ref) => {
  const {
    name,
    label,
    form,
    required,
    autoFocus,
    hideError = false,
    placeholder,
    className,
    textHidden,
    disabled = false,
    min,
    max,
    createdAt,
    allowedDays,
    ...rest
  } = props;

  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const [weekendError, setWeekendError] = useState("");
  const [dateRangeError, setDateRangeError] = useState("");

  const today = new Date().toISOString().split("T")[0];

  useImperativeHandle(ref, () => ({
    scrollIntoView: () => {
      wrapperRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    },
    focus: () => {
      inputRef.current?.focus();
      inputRef.current?.showPicker?.();
    },
  }));

  const formatDateForInput = (date) => {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // ✅ Add weekdays INCLUDING the first day
  const addWeekdaysIncludingStart = (startDate, daysToAdd) => {
    const date = new Date(startDate);
    let countedDays = 0;

    while (countedDays < daysToAdd) {
      const day = date.getDay();

      // Count only weekdays
      if (day !== 0 && day !== 6) {
        countedDays++;
      }

      if (countedDays < daysToAdd) {
        date.setDate(date.getDate() + 1);
      }
    }

    return date;
  };

  const calculateAllowedRange = () => {
    if (!createdAt || !allowedDays) return null;

    const created = new Date(createdAt);
    created.setHours(0, 0, 0, 0); // Normalize to start of day
    
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Normalize to start of day

    // Calculate original minDate from createdAt (for maxDate calculation)
    const originalMinDate = new Date(created);
    originalMinDate.setDate(originalMinDate.getDate() + 2);
    
    // Skip weekends for original minDate
    let originalDayOfWeek = originalMinDate.getDay();
    if (originalDayOfWeek === 0) {
      originalMinDate.setDate(originalMinDate.getDate() + 1);
    } else if (originalDayOfWeek === 6) {
      originalMinDate.setDate(originalMinDate.getDate() + 2);
    }

    // Max date is always calculated from original createdAt
    const maxDate = addWeekdaysIncludingStart(
      originalMinDate,
      parseInt(allowedDays)
    );

    // Use today's date for minDate if createdAt has already passed
    const baseDate = created < now ? now : created;
    
    // Min date = baseDate + 2 days (disable baseDate and the day after)
    const minDate = new Date(baseDate);
    minDate.setDate(minDate.getDate() + 2);

    // If minDate falls on weekend, move to next Monday
    const dayOfWeek = minDate.getDay();
    if (dayOfWeek === 0) {
      // Sunday -> add 1 day to get Monday
      minDate.setDate(minDate.getDate() + 1);
    } else if (dayOfWeek === 6) {
      // Saturday -> add 2 days to get Monday
      minDate.setDate(minDate.getDate() + 2);
    }

    return {
      minDate: formatDateForInput(minDate),
      maxDate: formatDateForInput(maxDate),
    };
  };

  const allowedRange = calculateAllowedRange();

  useEffect(() => {
    const currentValue = form.values[name];
    if (!currentValue && min) {
      const formattedDate = formatDateForInput(min);
      if (formattedDate) {
        form.setFieldValue(name, formattedDate, false);
      }
    }
  }, []);

  const handleClick = () => {
    inputRef.current?.showPicker?.();
  };

  const handleDateChange = (e, field) => {
    const selectedDate = e.target.value;
    setWeekendError("");
    setDateRangeError("");

    if (selectedDate) {
      const date = new Date(selectedDate + "T00:00:00");
      const dayOfWeek = date.getDay();

      if (dayOfWeek === 0 || dayOfWeek === 6) {
        form.setFieldValue(name, "");
        form.setFieldTouched(name, true);
        setWeekendError("Weekends (Saturday and Sunday) cannot be selected");
        return;
      }

      if (allowedRange) {
        const selected = new Date(selectedDate);
        const minDate = new Date(allowedRange.minDate);
        const maxDate = new Date(allowedRange.maxDate);

        if (selected < minDate || selected > maxDate) {
          form.setFieldValue(name, "");
          form.setFieldTouched(name, true);

          const minFormatted = minDate.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
          const maxFormatted = maxDate.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          });

          setDateRangeError(
            `Please select a date between ${minFormatted} and ${maxFormatted}`
          );
          return;
        }
      }
    }

    field.onChange(e);
  };

  const displayError =
    weekendError ||
    dateRangeError ||
    (form.touched[name] && form.errors[name]);

  const getCurrentValue = () => {
    const value = form.values[name];
    if (!value) return "";

    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }

    return formatDateForInput(value);
  };

  const getMinMaxDates = () => {
    if (allowedRange) {
      return {
        min: allowedRange.minDate,
        max: allowedRange.maxDate,
      };
    }
    return {
      min: min ? formatDateForInput(min) : today,
      max: max ? formatDateForInput(max) : undefined,
    };
  };

  const { min: minDate, max: maxDate } = getMinMaxDates();

  return (
    <div ref={wrapperRef} className="form-group w-full flex flex-col relative">
      <label
        htmlFor={name}
        className={`text-[19px] lg:text-21pt text-green text-nowrap ${
          textHidden ? "opacity-0" : ""
        } ${required ? "required" : "notRequired"} ${
          displayError ? "isInvalidText" : ""
        }`}
      >
        {label} {required && <span className="text-20pt text-orange">*</span>}
      </label>

      <Field name={name}>
        {({ field }) => (
          <input
            {...field}
            id={name}
            ref={inputRef}
            type="date"
            autoFocus={autoFocus}
            placeholder={placeholder}
            disabled={disabled}
            value={getCurrentValue()}
            min={minDate}
            max={maxDate}
            onClick={handleClick}
            onChange={(e) => handleDateChange(e, field)}
            className={`form-control bg-white font-mono w-full h-[56px] px-4 rounded-[15px] text-left border border-green mt-1 text-18pt
              focus:outline-none focus:ring-2 focus:ring-green focus:ring-opacity-50
              ${className ? className : "2.5xl:max-w-[354px]"}
              ${disabled ? "bg-gray-200 cursor-not-allowed" : ""}
              ${displayError ? "is-invalid" : ""}`}
            {...rest}
          />
        )}
      </Field>

      {!hideError && displayError && (
        <span id={`${name}-error`} className="errorMessage">
          {weekendError || dateRangeError || <ErrorMessage name={name} />}
        </span>
      )}

      {/* {allowedRange && (
        <p className="text-sm text-gray-600 mt-1 font-mono">
          Available dates:{" "}
          {new Date(allowedRange.minDate).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          })}{" "}
          -{" "}
          {new Date(allowedRange.maxDate).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}{" "}
          (weekdays only)
        </p>
      )} */}
    </div>
  );
});

export default CMSFormDateRange;